import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import {
  NotificationContent,
  NotificationContentKey,
} from '../models/NotificationContent'
import { notificationContentSchema } from '../models/schema'
import {
  fetchNotificationContentById,
  fetchNotificationContents,
} from '../services/NotificationContentService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<NotificationContent> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'notificationContent',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getNotificationContentsSuccess,
  setQuery: setNotificationContentQuery,
  reset: resetNotificationContentState,
  resetQueryAndIds: resetNotificationContentQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: NotificationContent }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectNotificationContents = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedNotificationContent = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectNotificationContentById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectNotificationContentsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )
export const selectNotificationContentByKey = (key: string) =>
  createSelector([selectRawItems], (items) =>
    Object.entries(items)
      .map((i) => i[1])
      .find((notificationContent) => notificationContent.key === key)
  )

/**
 * Actions
 */

export const setSelectedNotificationContent =
  (notificationContent: NotificationContent | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<NotificationContent>(notificationContent)
      )
    )

export const getNotificationContents = (
  notificationContents: NotificationContentKey[]
) =>
  SBAPI.FetchPaginatedDispatched(
    fetchNotificationContents(notificationContents),
    [notificationContentSchema],
    slice.actions
  )

export const getNotificationContentWithId = (id: number) =>
  SBAPI.FetchDispatched<NotificationContent>(
    fetchNotificationContentById(id),
    notificationContentSchema,
    slice.actions
  )
